import dayjs from "dayjs";
import { collection, doc, onSnapshot, Timestamp, updateDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import CustomSelect from "../components/common/CustomSelect";
import { Loading } from "../components/common/Loading";
import ComplaintForm from "../components/ComplaintForm";
import Navbar from "../components/header/Navbar";
import { useAuth } from "../hooks/useAuth";
import { db } from "../network/firebaseConfig";
import { CACHE_KEYS, getCachedData } from "../services/localStorageService";
import { Phone, ChevronUp, ChevronDown, ArrowUpDown, Wifi, Server } from 'lucide-react';

// const resolveTimeOptions = Array.from({ length: 24 }, (_, i) => `${i + 1} hour${i === 0 ? "" : "s"}`);

const resolveTimeOptions = [
    // Even hours from 2 to 24
    ...Array.from({ length: 12 }, (_, i) => `${(i + 1) * 2} hours`),
    // Days
    "1 day",
    "2 days",
    "3 days",
    // Weeks
    "1 week"
];

const ComplaintList = () => {
    const navigate = useNavigate();
    const { category: categoryParam } = useParams();

    const { user, loading: isLoading, users } = useAuth();
    const [complaints, setComplaints] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const statusOptions = ["in-operation", "completed", "paused"];

    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [selectedComplaint, setSelectedComplaint] = useState(null);
    const [completionComment, setCompletionComment] = useState("");
    const [selectedCategory, setSelectedCategory] = useState(categoryParam || null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });
    const [stats, setStats] = useState({
        total: 0,
        urgent: 0,
        delayed: 0,
        assigned: 0,
        unassigned: 0
    });

    const technicians = users.filter((u) => u.role === "technician");

    // Helper function to get value from onu/router (handle arrays)
    const getStatusValue = (value) => {
        if (Array.isArray(value)) {
            return value.length > 0 ? value[0] : '-';
        }
        return value || '-';
    };

    // Handle phone call
    const handlePhoneClick = (phoneNumber) => {
        if (!phoneNumber) return;
        if (window.confirm(`Do you want to call ${phoneNumber}?`)) {
            window.location.href = `tel:${phoneNumber}`;
        }
    };

    // Load complaints with real-time updates
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, "complaints"),
            (snapshot) => {
                let allComplaints = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Status filter
                allComplaints = allComplaints.filter(
                    (c) => c.status !== "completed" && c.status !== "paused"
                );

                // Role-based filtering
                if (user?.role === "technician") {
                    allComplaints = allComplaints.filter(
                        (c) => c.assignedTo === user.uid
                    );
                } else if (user?.role === "helpdesk") {
                    allComplaints = allComplaints.filter(
                        (c) => c.createdBy === user.uid
                    );
                }

                // Category filtering from URL param
                if (selectedCategory) {
                    allComplaints = allComplaints.filter(
                        (c) => c.category === selectedCategory
                    );
                }

                // Sorting - newest first
                allComplaints.sort(
                    (a, b) => b.createdAt?.seconds - a.createdAt?.seconds
                );

                // Calculate stats
                const now = new Date();
                let urgent = 0;
                let delayed = 0;
                let assigned = 0;
                let unassigned = 0;

                allComplaints.forEach(c => {
                    if (c.assignedTo) assigned++;
                    else unassigned++;

                    const hoursMatch = c.approximateResolveTime?.match(/(\d+)/);
                    if (hoursMatch && c.updatedAt) {
                        const totalHours = parseInt(hoursMatch[1]);
                        const updatedAt = c.updatedAt?.toDate?.() || new Date(c.updatedAt);
                        const workingHours = 13;
                        const modifiedHours = Math.min(totalHours, workingHours);
                        const resolveTime = new Date(updatedAt.getTime() + modifiedHours * 60 * 60 * 1000);

                        if (now > resolveTime) {
                            delayed++;
                        } else {
                            const remaining = resolveTime - now;
                            const hoursRemaining = remaining / (1000 * 60 * 60);
                            if (hoursRemaining < 1) {
                                urgent++;
                            }
                        }
                    }
                });

                setStats({
                    total: allComplaints.length,
                    urgent,
                    delayed,
                    assigned,
                    unassigned
                });

                setComplaints(allComplaints);
                setLoading(false);
            },
            (err) => {
                console.error(err);
                toast.error("Failed to load complaints");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user, selectedCategory]);

    useEffect(() => {
        const loadData = () => {
            try {
                const categories = getCachedData(CACHE_KEYS.CATEGORIES);
                if (categories) {
                    setCategories(categories);
                }
                setLoading(false);
            } catch (error) {
                console.error("Error loading data from cache:", error);
                setLoading(false);
            }
        };
        loadData();
    }, [categoryParam]);

    // Apply sorting
    useEffect(() => {
        if (sortConfig.key && complaints.length > 0) {
            const sorted = [...complaints];
            sorted.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (sortConfig.key === 'customerName') {
                    aVal = a.customerInfo?.name || '';
                    bVal = b.customerInfo?.name || '';
                } else if (sortConfig.key === 'ticketNumber') {
                    aVal = a.ticketNumber || '';
                    bVal = b.ticketNumber || '';
                } else if (sortConfig.key === 'category') {
                    aVal = a.category || '';
                    bVal = b.category || '';
                }

                if (aVal === undefined || aVal === null) aVal = '';
                if (bVal === undefined || bVal === null) bVal = '';

                if (typeof aVal === 'string') {
                    aVal = aVal.toLowerCase();
                    bVal = bVal.toLowerCase();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
            setComplaints(sorted);
        }
    }, [sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortIndicator = ({ column }) => {
        if (sortConfig.key !== column) {
            return <ArrowUpDown className="w-3 h-3 text-gray-400 inline ml-1" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp className="w-3 h-3 text-pink-500 inline ml-1" />
            : <ChevronDown className="w-3 h-3 text-pink-500 inline ml-1" />;
    };

    // Update URL when category changes
    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        if (category) {
            navigate(`/complaints/${category}`);
        } else {
            navigate("/complaints");
        }
    };

    // Assign technician
    const handleAssignTechnician = async (complaintId, technicianId) => {
        if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
            return toast.error("You do not have permission for this action");
        }
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);
            await updateDoc(complaintRef, {
                assignedTo: technicianId,
                status: "in-operation",
                updatedAt: Timestamp.now(),
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: Timestamp.now(),
                        message: `Assigned to technician (ID: ${technicianId})`,
                        userId: user.uid,
                    },
                ],
            });
            toast.success("Technician assigned & status updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to assign technician");
        }
    };

    // Change status
    const handleStatusChange = (complaintId, newStatus) => {
        if (
            user.role !== "admin" &&
            user.role !== "helpdesk" &&
            user.role !== "supervisor"
        ) {
            return toast.error("You do not have permission for this action");
        }

        const complaint = complaints.find((c) => c.id === complaintId);

        if (newStatus === "completed") {
            setSelectedComplaint(complaint);
            setShowCommentModal(true);
            return;
        }

        updateComplaintStatus(complaint, newStatus);
    };

    const updateComplaintStatus = async (complaint, newStatus, comment = "") => {
        try {
            const complaintRef = doc(db, "complaints", complaint.id);
            await updateDoc(complaintRef, {
                status: newStatus,
                updatedAt: Timestamp.now(),
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: Timestamp.now(),
                        message:
                            newStatus === "completed"
                                ? `Status changed to completed. Comment: ${comment}`
                                : `Status changed to ${newStatus}`,
                        userId: user.uid,
                    },
                ],
                ...(newStatus === "completed" && { completionComment: comment, resolvedAt: Timestamp.now() }),
            });
            toast.success("Status updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status");
        }
    };

    const handleTimeChange = async (complaintId, newTime) => {
        if (user.role !== "admin" && user.role !== "helpdesk" && user.role !== "supervisor") {
            return toast.error("You do not have permission for this action");
        }
        try {
            const complaintRef = doc(db, "complaints", complaintId);
            const complaint = complaints.find((c) => c.id === complaintId);
            await updateDoc(complaintRef, {
                approximateResolveTime: newTime,
                updatedAt: Timestamp.now(),
                activityLogs: [
                    ...(complaint.activityLogs || []),
                    {
                        time: Timestamp.now(),
                        message: `approximateResolveTime changed to ${newTime}`,
                        userId: user.uid,
                    },
                ],
            });
            toast.success("approximateResolveTime updated!");
        } catch (err) {
            console.error(err);
            toast.error("Failed to update approximateResolveTime");
        }
    };

    const StatusIndicator = ({ status }) => {
        const statusColors = {
            'in-operation': 'bg-blue-500',
            'completed': 'bg-green-500',
            'paused': 'bg-yellow-500'
        };
        return (
            <div className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${statusColors[status] || 'bg-gray-500'}`}></span>
                <span className="text-xs capitalize">{status}</span>
            </div>
        );
    };

    // Render ONU/Router status beautifully
    const renderNetworkStatus = (complaint) => {
        if (complaint.complaintType !== "Red signal") return null;

        return (
            <div className="mt-1.5 grid grid-cols-2 gap-1.5 text-[10px]">
                <div className="bg-blue-50/70 rounded p-1.5 border border-blue-100/50">
                    <div className="flex items-center gap-1 text-blue-700 font-medium text-[9px]">
                        <Server className="w-3 h-3" />
                        <span>ONU</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-gray-500 text-[8px]">PWR</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.power)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">PON</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.pon) === 'green' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.pon)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">LAN</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.onu?.lan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.onu?.lan)}
                        </span>
                    </div>
                </div>
                <div className="bg-purple-50/70 rounded p-1.5 border border-purple-100/50">
                    <div className="flex items-center gap-1 text-purple-700 font-medium text-[9px]">
                        <Wifi className="w-3 h-3" />
                        <span>Router</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-gray-500 text-[8px]">PWR</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.power) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.power)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">WLAN</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.wanLan) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.wanLan)}
                        </span>
                        <span className="text-gray-300 text-[8px]">|</span>
                        <span className="text-gray-500 text-[8px]">WiFi</span>
                        <span className={`font-semibold text-xs ${getStatusValue(complaint.router?.wifi) === 'on' ? 'text-green-600' : 'text-red-600'}`}>
                            {getStatusValue(complaint.router?.wifi)?.slice(0, 8)}
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    if (loading || isLoading) return <Loading />;

    return (
        <>
            <Navbar user={user} />
            {isFormVisible && (
                <ComplaintForm
                    onClose={() => setIsFormVisible(false)}
                    initialCategory={selectedCategory}
                />
            )}

            {!complaints.length ? (
                !isFormVisible && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-4">
                        <div className="text-center">
                            <div className="text-6xl mb-4">📭</div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-700">
                                No Active Complaints Found
                            </h2>
                            {selectedCategory && (
                                <p className="text-gray-500 mt-2">
                                    No complaints in <span className="font-semibold text-blue-600">{selectedCategory}</span> category
                                </p>
                            )}
                            <p className="text-gray-400 mt-1 text-sm">
                                {selectedCategory ? 'Try clearing the filter or' : 'Get started by'} creating a new complaint
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-3">
                            {user && (user.role === "admin" || user.role === "helpdesk" || user.role === "supervisor") && (
                                <button
                                    className="bg-gradient-to-r from-pink-500 to-rose-500 text-white py-2.5 px-6 rounded-lg hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg hover:shadow-pink-500/25"
                                    onClick={() => setIsFormVisible(true)}
                                >
                                    + Create Complaint
                                </button>
                            )}
                            {selectedCategory && (
                                <button
                                    onClick={() => handleCategoryChange(null)}
                                    className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                >
                                    Clear Filter
                                </button>
                            )}
                        </div>
                    </div>
                )
            ) : (
                !isFormVisible && (
                    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 space-y-4">
                        {/* Header with Stats */}
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap justify-between items-center gap-3">
                                <div>
                                    <h1 className="text-xl font-bold text-gray-800">Active Complaints</h1>
                                    <p className="text-xs text-gray-500">Real-time monitoring of all in-operation complaints</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Auto-refresh</span>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                                        Live
                                    </span>
                                </div>
                            </div>

                            {/* Stats Cards */}
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                                    <p className="text-[10px] text-gray-500 font-medium">Total Active</p>
                                    <p className="text-xl font-bold text-gray-800">{stats.total}</p>
                                </div>
                                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                                    <p className="text-[10px] text-gray-500 font-medium">Urgent</p>
                                    <p className={`text-xl font-bold ${stats.urgent > 0 ? 'text-orange-600' : 'text-gray-800'}`}>
                                        {stats.urgent}
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                                    <p className="text-[10px] text-gray-500 font-medium">Delayed</p>
                                    <p className={`text-xl font-bold ${stats.delayed > 0 ? 'text-red-600' : 'text-gray-800'}`}>
                                        {stats.delayed}
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                                    <p className="text-[10px] text-gray-500 font-medium">Assigned</p>
                                    <p className="text-xl font-bold text-green-600">{stats.assigned}</p>
                                </div>
                                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
                                    <p className="text-[10px] text-gray-500 font-medium">Unassigned</p>
                                    <p className={`text-xl font-bold ${stats.unassigned > 0 ? 'text-yellow-600' : 'text-gray-800'}`}>
                                        {stats.unassigned}
                                    </p>
                                </div>
                            </div>

                            {/* Filters */}
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="w-44">
                                    <CustomSelect
                                        name="category"
                                        label=""
                                        options={categories.map(c => c.name)}
                                        value={selectedCategory}
                                        placeholder="Filter by Category"
                                        onChange={(name, value) => handleCategoryChange(value)}
                                    />
                                </div>
                                {selectedCategory && (
                                    <button
                                        onClick={() => handleCategoryChange(null)}
                                        className="px-2.5 py-1 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                    >
                                        Clear Filter ×
                                    </button>
                                )}
                                <span className="text-xs text-gray-400 ml-auto">
                                    {complaints.length} complaints
                                </span>
                            </div>
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow border border-gray-100">
                            <table className="w-full table-auto text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[70px]">
                                            <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('createdAt')}>
                                                Date <SortIndicator column="createdAt" />
                                            </span>
                                        </th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[90px]">
                                            <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('ticketNumber')}>
                                                Ticket <SortIndicator column="ticketNumber" />
                                            </span>
                                        </th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[100px]">
                                            <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('customerName')}>
                                                Customer <SortIndicator column="customerName" />
                                            </span>
                                        </th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[100px]">
                                            <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('category')}>
                                                Category <SortIndicator column="category" />
                                            </span>
                                        </th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider flex-1 flex-grow">Description</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[130px]">
                                            <span className="cursor-pointer hover:text-gray-700" onClick={() => handleSort('assignedTo')}>
                                                Assigned To <SortIndicator column="assignedTo" />
                                            </span>
                                        </th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[120px]">Est. Time</th>
                                        <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider w-[120px]">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {complaints.map((c, index) => (
                                        <tr key={c.id} className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                                            <td className="px-2 py-2 whitespace-nowrap text-gray-600 text-xs">
                                                {dayjs(c.createdAt?.toDate()).format("DD-MM-YY")}
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap font-mono text-xs font-semibold text-gray-700">
                                                {c.ticketNumber}
                                            </td>
                                            <td className="px-2 py-2 truncate max-w-[120px]" title={c.customerInfo?.name}>
                                                <div>
                                                    <div className="flex items-center gap-1">
                                                        <p className="text-sm font-medium text-gray-800 truncate">{c.customerInfo?.name}</p>
                                                        {c.customerInfo?.phone && (
                                                            <button
                                                                onClick={() => handlePhoneClick(c.customerInfo.phone)}
                                                                className="p-0.5 text-blue-500 hover:text-blue-700 transition flex-shrink-0"
                                                                title={`Call ${c.customerInfo.phone}`}
                                                            >
                                                                <Phone className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-[10px] text-gray-400">{c.customerInfo?.phone}</p>
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 whitespace-nowrap">
                                                <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-medium">
                                                    {c?.category || "-"}
                                                </span>
                                            </td>
                                            <td className="px-2 py-2">
                                                {c?.description && (
                                                    <p className="text-xs text-gray-600 line-clamp-2">{c.description}</p>
                                                )}
                                                {renderNetworkStatus(c)}
                                            </td>
                                            <td className="px-2 py-2 min-w-[120px]">
                                                <select
                                                    value={c.assignedTo || ""}
                                                    onChange={(e) => handleAssignTechnician(c.id, e.target.value)}
                                                    className="w-full border border-gray-300 rounded px-1.5 py-1 text-xs focus:ring-1 focus:ring-blue-400 focus:border-transparent outline-none transition"
                                                    title={users.find(u => u.uid === c.assignedTo)?.name}
                                                >
                                                    <option value="">Assign</option>
                                                    {users.map((user) => (
                                                        <option key={user.uid} value={user.uid}>
                                                            {user.name} ({user.department})
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2 min-w-[110px]">
                                                <CustomSelect
                                                    name="approximateResolveTime"
                                                    placeholder="Select"
                                                    options={resolveTimeOptions}
                                                    value={c.approximateResolveTime}
                                                    onChange={(name, value) => handleTimeChange(c.id, value)}
                                                    className="text-xs"
                                                />
                                            </td>
                                            <td className="px-2 py-2 min-w-[110px]">
                                                <CustomSelect
                                                    name="status"
                                                    placeholder="Change status"
                                                    options={statusOptions}
                                                    value={c.status}
                                                    onChange={(name, value) => handleStatusChange(c.id, value)}
                                                    className="text-xs"
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden space-y-3">
                            {complaints.map((c) => (
                                <div key={c.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                                    <div
                                        className="p-3 bg-gray-50/50 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                                        onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="font-mono text-[10px] bg-gray-200 px-1.5 py-0.5 rounded font-semibold">
                                                    {c.ticketNumber}
                                                </span>
                                                <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-full text-[10px] font-medium">
                                                    {c.category || '-'}
                                                </span>
                                            </div>
                                            <div className="mt-1 truncate">
                                                <div className="flex items-center gap-1">
                                                    <div className="font-medium text-sm text-gray-800 truncate">{c.customerInfo?.name}</div>
                                                    {c.customerInfo?.phone && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePhoneClick(c.customerInfo.phone);
                                                            }}
                                                            className="p-0.5 text-blue-500 hover:text-blue-700 transition flex-shrink-0"
                                                            title={`Call ${c.customerInfo.phone}`}
                                                        >
                                                            <Phone className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-gray-500 truncate">{c.customerInfo?.phone}</div>
                                            </div>
                                        </div>
                                        <div className="text-right flex-shrink-0 ml-2">
                                            <StatusIndicator status={c.status} />
                                            <div className="text-[10px] text-gray-400 mt-0.5">
                                                {dayjs(c.createdAt?.toDate()).format("DD/MM/YY")}
                                            </div>
                                            <svg
                                                className={`w-4 h-4 mt-1 mx-auto text-gray-400 transition-transform duration-200 ${expandedRow === c.id ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>

                                    {expandedRow === c.id && (
                                        <div className="p-3 space-y-3 bg-white">
                                            {c?.description && (
                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Description</label>
                                                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg border border-gray-100">
                                                        {c.description}
                                                    </p>
                                                </div>
                                            )}
                                            {renderNetworkStatus(c)}
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Est. Time</label>
                                                    <CustomSelect
                                                        name="approximateResolveTime"
                                                        placeholder="Select"
                                                        options={resolveTimeOptions}
                                                        value={c.approximateResolveTime}
                                                        onChange={(name, value) => handleTimeChange(c.id, value)}
                                                        className="text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Status</label>
                                                    <CustomSelect
                                                        name="status"
                                                        placeholder="Change"
                                                        options={statusOptions}
                                                        value={c.status}
                                                        onChange={(name, value) => handleStatusChange(c.id, value)}
                                                        className="text-xs"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">Assign Technician</label>
                                                <select
                                                    value={c.assignedTo || ""}
                                                    onChange={(e) => handleAssignTechnician(c.id, e.target.value)}
                                                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 focus:border-transparent outline-none transition bg-white"
                                                >
                                                    <option value="">Select</option>
                                                    {technicians.map((tech) => (
                                                        <option key={tech.uid} value={tech.uid}>
                                                            {tech.name} ({tech.department})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Comment Modal */}
                        {showCommentModal && (
                            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center z-50 px-4 animate-fade-in">
                                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 transform transition-all">
                                    <div className="text-center mb-4">
                                        <div className="text-4xl mb-2">✅</div>
                                        <h3 className="text-xl font-bold text-gray-800">Complete Complaint</h3>
                                        <p className="text-sm text-gray-500 mt-1">Add a comment about how this was resolved</p>
                                    </div>
                                    <textarea
                                        value={completionComment}
                                        onChange={(e) => setCompletionComment(e.target.value)}
                                        placeholder="Describe the resolution steps taken..."
                                        className="w-full border border-gray-300 rounded-xl p-3 mb-4 focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none transition resize-none"
                                        rows={4}
                                    />
                                    <div className="flex flex-col sm:flex-row justify-end gap-2">
                                        <button
                                            onClick={() => {
                                                setShowCommentModal(false);
                                                setCompletionComment("");
                                            }}
                                            className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition order-2 sm:order-1 font-medium"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (!completionComment.trim()) {
                                                    toast.error("Please add a comment before completing.");
                                                    return;
                                                }
                                                updateComplaintStatus(selectedComplaint, "completed", completionComment);
                                                setShowCommentModal(false);
                                                setCompletionComment("");
                                                setSelectedComplaint(null);
                                            }}
                                            className="px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition order-1 sm:order-2 mb-2 sm:mb-0 font-medium shadow-lg hover:shadow-blue-500/25"
                                        >
                                            Complete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
        </>
    );
};

export default ComplaintList;